import BaseModal from "../../../../components/ui/BaseModal";
import BaseTextField from "../../../../components/ui/BaseTextField";
import BaseButton from "../../../../components/ui/BaseButton";
import BaseAutoSelect from "../../../../components/ui/BaseAutoSelect";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../../../../store/toast/toastSlice";
import { fetchCompanyUsers } from "../../../../services/api/company";
import { createTeam, updateTeam } from "../../../../services/api/teams";
import { fetchDepartments } from "../../../../services/api/departments";
import BaseDataTableV2 from "../../../../components/ui/BaseDataTableV2";

import { formatPhoneNumber } from "../../../../utils/helpers";
const getValidationSchema = () =>
  yup.object({
    teamName: yup
      .string()
      .trim()
      .required("Enter Team name")
      .max(100, "Name must be at most 100 characters"),
    teamEmail: yup.string().trim().email("Enter a valid email").required("Enter team email"),
    members: yup.array().min(1, "Select at least one member"),
    departmentId: yup.number().typeError("Select a department").required("Select a department"),
  });

const AddEditTeamModal = ({
  open,
  close,
  selectedTeam,
  setSelectedTeam,
  refetchAllTeams,
  setViewTeam,
  viewTeam,
}) => {
  const dispatch = useDispatch();

  const { data: companyUsers } = useQuery({
    queryKey: ["companyUsers"],
    queryFn: () => fetchCompanyUsers(),
  });

  const { data: departmentsData } = useQuery({
    queryKey: ["allDepartmentsForTeams"],
    queryFn: () => fetchDepartments({}),
  });

  const { mutateAsync: createTeamMutation, isLoading: isCreating } = useMutation({
    mutationFn: createTeam,
  });

  const { mutateAsync: updateTeamMutation, isLoading: isUpdating } = useMutation({
    mutationFn: updateTeam,
  });

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderCell: (params) => (
        <p className="text-primary">
          {params.row.teamMember.firstName} {params.row.teamMember.lastName}
        </p>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      renderCell: (params) => <p>{params.row.teamMember.email || "-"}</p>,
    },
    {
      field: "phone",
      headerName: "Phone Number",
      flex: 1,
      renderCell: (params) => <p>{formatPhoneNumber(params.row.teamMember.phoneNumber) || "-"}</p>,
    },
  ];

  const handleSubmit = async (values) => {
    try {
      if (selectedTeam) {
        await updateTeamMutation({
          id: selectedTeam.id,
          name: values.teamName,
          email: values.teamEmail.trim(),
          members: values.members,
          departmentId: values.departmentId || null,
        });
        refetchAllTeams?.();
        dispatch(showToast({ message: "Team updated successfully", type: "success" }));
      } else {
        await createTeamMutation({
          name: values.teamName,
          email: values.teamEmail.trim(),
          members: values.members,
          departmentId: values.departmentId || null,
        });
        dispatch(showToast({ message: "Team created successfully", type: "success" }));
      }
      refetchAllTeams?.();
      setSelectedTeam(null);
      close(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || error.message || "Something went wrong",
          type: "error",
        }),
      );
    }
  };
  const formik = useFormik({
    initialValues: {
      teamName: selectedTeam?.name || "",
      teamEmail: selectedTeam?.email || "",
      members: selectedTeam?.members?.map((m) => m.teamMemberId) || [],
      departmentId: selectedTeam?.departmentId || "",
    },
    validationSchema: getValidationSchema(),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <BaseModal
        open={open}
        close={() => {
          close(false);
          setSelectedTeam(null);
          setViewTeam(null);
        }}
        className="overflow-auto"
        style={{ width: "1000px" }}
      >
        <div className="px-8 py-5">
          <h6 className="text-dark font-medium border-b pb-1">
            {viewTeam ? "View Team" : selectedTeam ? "Update Team" : "Create New Team"}
          </h6>
        </div>

        <div className="px-8 py-4 grid grid-cols-2 gap-x-6 gap-y-4">
          <div className="col-span-2">
            <BaseTextField
              disabled={viewTeam ? true : false}
              label={
                <span>
                  Team Name <span className="text-red-500">*</span>
                </span>
              }
              labelShrink={false}
              optional={false}
              id="teamName"
              name="teamName"
              disableRowSelectionOnClick={true}
              value={formik.values.teamName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.teamName && Boolean(formik.errors.teamName)}
              helperText={formik.touched.teamName && formik.errors.teamName}
            />
          </div>

          <div className="col-span-2">
            {/* A customer serviced by this team inherits the address as its team
                email when an admin approves the profile. */}
            <BaseTextField
              disabled={viewTeam ? true : false}
              label={
                <span>
                  Team Email <span className="text-red-500">*</span>
                </span>
              }
              labelShrink={false}
              optional={false}
              id="teamEmail"
              name="teamEmail"
              value={formik.values.teamEmail}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.teamEmail && Boolean(formik.errors.teamEmail)}
              helperText={formik.touched.teamEmail && formik.errors.teamEmail}
            />
          </div>

          <div className="col-span-2">
            <BaseAutoSelect
              required
              disabled={viewTeam ? true : false}
              labelShrink={false}
              label={
                <span>
                  Department <span className="text-red-500">*</span>
                </span>
              }
              placeholder="Select a department"
              options={
                departmentsData?.data?.data?.map((dept) => ({
                  label: dept.name,
                  value: dept.id,
                })) || []
              }
              value={formik.values.departmentId}
              onChange={(e) => formik.setFieldValue("departmentId", e.target.value)}
            />
            {formik.touched.departmentId && formik.errors.departmentId && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.departmentId}</p>
            )}
          </div>

          {viewTeam ? (
            <div className="col-span-2 mb-5">
              <p className="mb-4">Team Members:</p>
              <BaseDataTableV2
                className="max-h-[45vh]"
                rowHeight={46}
                columns={columns}
                data={selectedTeam?.members || []}
                isLoading={false}
                disableColumnResize={false}
                disableRowSelectionOnClick={true}
                hideFooter={true}
                isCellEditable={(params) => false}
              />
            </div>
          ) : (
            <div className="col-span-2 mb-5">
              <BaseAutoSelect
                required
                labelShrink={false}
                label="Team Members"
                multiple
                placeholder="Select team members"
                options={
                  companyUsers?.data?.users?.map((user) => ({
                    label: `${user.firstName} ${user.lastName}`,
                    value: user.id,
                  })) || []
                }
                value={formik.values.members}
                onChange={(e) => formik.setFieldValue("members", e.target.value)}
                isClearable
                closeMenuOnSelect={false}
              />
              {formik.touched.members && formik.errors.members && (
                <p className="text-red-500 text-sm mt-1">{formik.errors.members}</p>
              )}
            </div>
          )}
        </div>

        <div
          className="d-flex content-center bg-white py-6 mt-6"
          style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
        >
          <BaseButton
            className="px-[28px] py-[5px] text-primary hover:!text-white"
            variant="outlined"
            color="primary"
            style={{ marginRight: "16px" }}
            onClick={() => {
              close(false);
              setSelectedTeam(null);
              setViewTeam(null);
            }}
          >
            Cancel
          </BaseButton>
          {!viewTeam && (
            <BaseButton
              disabled={!formik.dirty || !formik.isValid || isCreating || isUpdating}
              onClick={formik.handleSubmit}
              loaderSize="22px"
              isLoading={isCreating || isUpdating}
              color="primary"
              className="py-[5px] px-[20px] min-w-32"
            >
              {selectedTeam ? "Update" : "Create"}
            </BaseButton>
          )}
        </div>
      </BaseModal>
    </form>
  );
};

export default AddEditTeamModal;
